<script setup lang="ts">
import type { RouteLocationRaw } from 'vue-router';
import type { Size } from '../types/size';
import { ref, watch } from 'vue';
import Icon from './Icon.vue';

interface Props {
	/** Stable `data-testid` for E2E selectors. Rendered on the interactive element, not the wrapper. */
	testId?: string;
	label?: string;
	type?: 'button' | 'submit';
	severity?: 'primary' | 'basic';
	variant?: 'solid' | 'outline' | 'soft' | 'empty';
	size?: Extract<Size, 'xs' | 'sm' | 'md'>;
	isRounded?: boolean;
	isWide?: boolean;
	icon?: string;
	iconPosition?: 'start' | 'end';
	to?: RouteLocationRaw;
	target?: string;
	external?: boolean;
	loading?: boolean;
	disabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
	type: 'button',
	severity: 'primary',
	variant: 'empty',
	size: 'md',
	isRounded: true,
	iconPosition: 'end',
});

defineEmits<{
	click: [MouseEvent];
}>();

const hasFinishedLoading = ref(false);

watch(() => props.loading, (newVal, oldVal) => {
	if (!newVal && oldVal) {
		hasFinishedLoading.value = true;
	}
	else if (newVal) {
		hasFinishedLoading.value = false;
	}
});
</script>

<template>
	<button
		:type="props.type"
		class="btn"
		:class="[{
			[`btn--${props.severity}`]: props.severity,
			[`btn--${props.variant}`]: props.variant,
			[`btn--${props.size}`]: props.size,
			'btn--is-rounded': props.isRounded,
			'btn--is-wide': props.isWide,
			'btn--loading': props.loading,
			'btn--loading-finished': hasFinishedLoading,
			'btn--disabled': props.disabled || props.loading,
		}]"
		:data-testid="props.testId"
		v-bind="$attrs"
		:disabled="props.disabled || props.loading"
		v-if="!to"
		@click="$emit('click', $event)"
	>
		<Icon :name="props.icon" v-if="props.icon && props.iconPosition === 'start'" />
		<span class="btn__text" v-if="props.label">
			{{ props.label }}
		</span>
		<Icon :name="props.icon" v-if="props.icon && props.iconPosition === 'end'" />
		<span class="btn__loading"></span>
	</button>

	<NuxtLink
		class="btn"
		:class="[{
			[`btn--${props.severity}`]: props.severity,
			[`btn--${props.variant}`]: props.variant,
			[`btn--${props.size}`]: props.size,
			'btn--is-rounded': props.isRounded,
			'btn--is-wide': props.isWide,
			'btn--disabled': props.disabled || props.loading,
			'btn--loading': props.loading,
			'btn--loading-finished': hasFinishedLoading,
		}]"
		:to="props.disabled ? undefined : props.to"
		:target="props.target"
		:external="props.external"
		:rel="props.target ? 'noopener noreferrer' : null"
		:data-testid="props.testId"
		v-bind="$attrs"
		v-else
	>
		<Icon :name="props.icon" v-if="props.icon && props.iconPosition === 'start' && !props.loading" />
		<span class="btn__text" v-if="props.label">
			{{ props.label }}
		</span>
		<Icon :name="props.icon" v-if="props.icon && props.iconPosition === 'end'" />
		<span class="btn__loading"></span>
	</NuxtLink>
</template>
